// @flow strict

import React, { useMemo } from 'react';

import Fuse from 'fuse.js';

import type { PostNode } from '~types/post';

/**
 * Hook to run post search.
 *
 * @param {Array<PostNode>} posts - data to search
 * @param {string} searchValue - value to search within search search data
 * @param {Array<string>} categories - categories to filter for
 * @param {Array<string>} tags - tags to filter for
 *
 * @returns {Array<PostNode>}
 */
export const usePostSearch = (
  posts: Array<PostNode>,
  searchValue: string,
  categories?: Array<string> = [],
  tags?: Array<string> = [],
): Array<PostNode> => {
  // create search data
  const searchData = useMemo(() => {
    const options = {
      includeScore: true,
      shouldSort: true,
      keys: [
        'frontmatter.title',
        'frontmatter.description',
        'fields.authors.name',
        'html',
      ],
    };

    return new Fuse(posts, options);
  }, [posts]);

  // if search term is empty, use all posts
  let result =
    searchValue === ''
      ? posts
      : searchData.search(searchValue).map((i) => i.item);

  // if a filter is set, filter posts
  if (categories.length !== 0 || tags.length !== 0) {
    result = result.filter((post) => {
      // no category/filter given
      if (categories.length === 0 && tags.length === 0) {
        return true;
      } else if (categories.length !== 0 && tags.length === 0) {
        // filter for categories only
        return categories.includes(post.frontmatter.category);
      } else if (categories.length === 0 && tags.length !== 0) {
        // filter tags only
        return tags.find((tag) => (post.frontmatter.tags || []).includes(tag));
      } else {
        // filter for tags AND category
        return (
          categories.includes(post.frontmatter.category) &&
          tags.find((tag) => (post.frontmatter.tags || []).includes(tag))
        );
      }
    });
  }

  return result;
};
